/**
 * QrfDialog: the Client history (LP-33; the Quick Reference's new name),
 * the record over time as its own printable document.
 * The Face Sheet is the client as of today (a balance sheet); this is what
 * changed (the P&L and cash flow), newest first in every section:
 *   - current medications, each with its dated changes and any stop date;
 *   - medications starting later, with their start dates (LP-41; the
 *     section prints only when there is one);
 *   - past medications, with started, stopped and the dose span, and
 *     their changes;
 *   - every hospital event;
 *   - resolved conditions;
 *   - status changes: the standing status and its note, the date of
 *     death, the hospice start and the service start.
 * Every section prints its heading, with "None recorded" under an empty
 * one.
 *
 * Data: the record snapshot the page already fetched, plus one history read
 * per medication shown (GET .../record/medications/{rowId}/history, audited
 * on the server: one phi_access_log row per medication, carrying its row id
 * and the entry count, never a dose). The reads run only while the dialog is
 * open and share the View history drawer's cache.
 *
 * Print and PDF go through PrintableSheetDialog, under the same print scope
 * and page-break rules as the Face Sheet. The component, file and test ids
 * keep the qrf name (the cache and localStorage rule: never rename a key).
 */

import type { ReactNode } from 'react';
import { useQueries } from '@tanstack/react-query';
import { ErrorBanner } from '@/components/ErrorBanner';
import { proseDate } from '@/lib/proseDate';
import { medicationTiming, stopsOn } from '@/lib/medicationTiming';
import { practiceDayOf } from '@/lib/practiceDate';
import type { ClientRecordSnapshot, MedicationRow } from '@/lib/clientRecord';
import { displayValue } from '@/lib/valueCase';
import { valueLabel } from '@/lib/recordFormWords';
import {
  ageOn,
  clientHistoryStatusLines,
  faceSheetMedications,
  faceSheetPastMedications,
  faceSheetResolvedConditions,
  qrfHospitalEvents,
} from '@/lib/faceSheet';
import { PrintableSheetDialog, SheetList, SheetSection, doseSpan, hospitalLine } from './printableSheet';
import {
  entryHeadline,
  fetchMedicationHistory,
  medicationHistoryKey,
  type MedicationHistory,
} from './MedicationHistory';

/** The document's title, shared with the hero menu's test. */
export const CLIENT_HISTORY_TITLE = 'Client history';

const newestFirst = (a: string, b: string): number => b.localeCompare(a);

/** One medication's changes as dated lines, newest first; the creation is
 *  left out (the started date is on the medication itself). */
function changeLines(h: MedicationHistory): Array<{ key: string; text: string }> {
  return h.entries
    .filter((e) => e.kind !== 'added')
    .map((e, i) => {
      const day = practiceDayOf(e.at);
      return { key: e.event_id ?? `entry-${i}`, at: e.at, text: `${day ? proseDate(day) : 'Undated'}: ${entryHeadline(e)}` };
    })
    .sort((a, b) => newestFirst(a.at, b.at))
    .map(({ key, text }) => ({ key, text }));
}

/** A current medication's own line: dose, times of day, schedule, started,
 *  and the stop date when it stops on a later day (LP-41). */
function currentLine(m: MedicationRow, today: string): string {
  const stops = stopsOn(m, today);
  return [
    m.dose ? displayValue(m.dose, 'medication') : null,
    (m.timesOfDay ?? []).map((t) => valueLabel('medications', 'times_of_day', t)).join(', ') || null,
    m.schedule ? displayValue(m.schedule, 'medication') : null,
    m.startedOn ? `started ${proseDate(m.startedOn)}` : null,
    stops ? `stops ${proseDate(stops)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** A medication starting later: dose, times of day, schedule, starts. */
function startingLine(m: MedicationRow): string {
  return [
    m.dose ? displayValue(m.dose, 'medication') : null,
    (m.timesOfDay ?? []).map((t) => valueLabel('medications', 'times_of_day', t)).join(', ') || null,
    m.schedule ? displayValue(m.schedule, 'medication') : null,
    m.startedOn ? `starts ${proseDate(m.startedOn)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** A past medication's own line: the dose span, started and stopped. */
function pastLine(m: MedicationRow): string {
  return [
    m.dose ? doseSpan(m) : null,
    m.startedOn ? `started ${proseDate(m.startedOn)}` : null,
    m.endedOn ? `stopped ${proseDate(m.endedOn)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** The dated change lines under a medication, or the loading / refused /
 *  none words. One line per change, newest first. */
function ChangesUnder({ state }: { state: ChangesState }): ReactNode {
  if (state.kind === 'loading') return <p className="pl-3 text-[0.71875rem] italic text-ink-soft">Loading the changes…</p>;
  if (state.kind === 'failed') return null;
  if (state.lines.length === 0) return <p className="pl-3 text-[0.71875rem] italic text-ink-soft">No changes recorded</p>;
  return (
    <ul className="mt-0.5 pl-3 text-[0.75rem]" data-testid="qrf-med-changes">
      {state.lines.map((l) => (
        <li key={l.key}>{l.text}</li>
      ))}
    </ul>
  );
}

type ChangesState =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'loaded'; lines: Array<{ key: string; text: string }> };

/** Both medication sections, sharing one history read per medication not
 *  archived. A refused read shows the error copy once, above the lists;
 *  the medications still print with their own facts. */
function MedicationSections({
  tenantSlug,
  clientId,
  current,
  starting,
  past,
  today,
}: {
  tenantSlug: string;
  clientId: string;
  current: MedicationRow[];
  starting: MedicationRow[];
  past: MedicationRow[];
  /** The practice day: the stop words (LP-41). */
  today: string;
}): ReactNode {
  const all = [...current, ...starting, ...past];
  const results = useQueries({
    queries: all.map((m) => ({
      queryKey: medicationHistoryKey(tenantSlug, clientId, m.id),
      queryFn: ({ signal }: { signal: AbortSignal }) => fetchMedicationHistory(tenantSlug, clientId, m.id, signal),
    })),
  });
  const failed = results.find((r) => r.isError);
  const stateOf = (m: MedicationRow): ChangesState => {
    const r = results[all.indexOf(m)];
    if (r === undefined || r.isError) return { kind: 'failed' };
    if (!r.data) return { kind: 'loading' };
    return { kind: 'loaded', lines: changeLines(r.data) };
  };

  return (
    <>
      {failed && (
        <div className="mt-3">
          <ErrorBanner error={failed.error} what="the medication changes" data-testid="qrf-med-changes-error" />
        </div>
      )}
      <SheetSection title="Current medications" testId="qrf-current-medications" className="mt-3">
        <SheetList
          empty="None recorded"
          items={current.map((m) => ({
            key: m.id,
            body: (
              <div data-testid="qrf-current-medication">
                <span className="font-semibold">{displayValue(m.name, 'medication')}</span>
                {currentLine(m, today) !== '' && <span className="text-ink-soft">{` · ${currentLine(m, today)}`}</span>}
                {m.prescriber && <span className="text-ink-soft">{` · ${m.prescriber}`}</span>}
                <ChangesUnder state={stateOf(m)} />
              </div>
            ),
          }))}
        />
      </SheetSection>

      {starting.length > 0 && (
        <SheetSection title="Starting soon" testId="qrf-starting-medications" className="mt-3">
          <SheetList
            empty="None recorded"
            items={starting.map((m) => ({
              key: m.id,
              body: (
                <div data-testid="qrf-starting-medication">
                  <span className="font-semibold">{displayValue(m.name, 'medication')}</span>
                  {startingLine(m) !== '' && <span className="text-ink-soft">{` · ${startingLine(m)}`}</span>}
                  {m.prescriber && <span className="text-ink-soft">{` · ${m.prescriber}`}</span>}
                  <ChangesUnder state={stateOf(m)} />
                </div>
              ),
            }))}
          />
        </SheetSection>
      )}

      <SheetSection title="Past medications" testId="qrf-past-medications" className="mt-3">
        <SheetList
          empty="None recorded"
          items={past.map((m) => ({
            key: m.id,
            body: (
              <div data-testid="qrf-past-medication">
                <span className="font-semibold">{displayValue(m.name, 'medication')}</span>
                {pastLine(m) !== '' && <span className="text-ink-soft">{` · ${pastLine(m)}`}</span>}
                <ChangesUnder state={stateOf(m)} />
              </div>
            ),
          }))}
        />
      </SheetSection>
    </>
  );
}

export function QrfDialog({
  record,
  today,
  tenantSlug,
  onClose,
}: {
  record: ClientRecordSnapshot;
  /** The page's practice day (the sheet never reads the clock). */
  today: string;
  tenantSlug: string;
  onClose: () => void;
}): ReactNode {
  const { vitals } = record;
  const hospitals = qrfHospitalEvents(record.hospitalEvents);
  const resolvedConditions = faceSheetResolvedConditions(record.conditions);
  // Current medications: the most recently started first.
  const current = [...faceSheetMedications(record.medications, today)].sort((a, b) =>
    newestFirst(a.startedOn ?? '', b.startedOn ?? ''),
  );
  // Starting later (LP-41): the soonest start first.
  const starting = record.medications
    .filter((m) => m.isActive && medicationTiming(m, today) === 'starting_soon')
    .sort((a, b) => (a.startedOn ?? '').localeCompare(b.startedOn ?? ''));
  const past = faceSheetPastMedications(record.medications, today);
  const statusLines = clientHistoryStatusLines(vitals, proseDate);

  return (
    <PrintableSheetDialog
      title={CLIENT_HISTORY_TITLE}
      blurb="The record over time: medications and their changes, past medications, hospital events, resolved conditions and status changes."
      testIdPrefix="qrf"
      printElementId="qrf-print"
      pdfStem="ClientHistory"
      clientName={vitals.displayName}
      onClose={onClose}
    >
      <div id="qrf-print" data-testid="qrf" className="text-ink">
        <header className="border-b-2 border-sage-800 pb-3">
          <p className="text-[0.625rem] font-semibold uppercase tracking-[0.16em] text-ink-soft">Client history · Client</p>
          <h2 className="font-display text-[1.625rem] leading-tight" data-testid="qrf-name">
            {vitals.displayName}
            {vitals.preferredName?.trim() && ` (${vitals.preferredName.trim()})`}
          </h2>
          <p className="mt-0.5 text-[0.8125rem]">
            {`${vitals.dob ? `DOB ${proseDate(vitals.dob)} (age ${ageOn(vitals.dob, today)})` : 'DOB not recorded'}; Prepared ${proseDate(today)}`}
          </p>
        </header>

        <MedicationSections tenantSlug={tenantSlug} clientId={record.clientId} current={current} starting={starting} past={past} today={today} />

        <SheetSection title="Hospital events" testId="qrf-hospital-events" className="mt-3">
          <SheetList empty="None recorded" items={hospitals.map((h) => ({ key: h.id, body: hospitalLine(h) }))} />
        </SheetSection>

        <SheetSection title="Resolved conditions" testId="qrf-resolved-conditions" className="mt-3">
          <SheetList
            empty="None recorded"
            items={resolvedConditions.map((c) => ({
              key: c.id,
              body: (
                <>
                  <span className="font-semibold">{displayValue(c.name, 'diagnosis')}</span>
                  {c.notedOn && <span className="text-ink-soft">{` · noted ${proseDate(c.notedOn)}`}</span>}
                </>
              ),
            }))}
          />
        </SheetSection>

        <SheetSection title="Status changes" testId="qrf-status-changes" className="mt-3">
          <SheetList empty="None recorded" items={statusLines.map((l) => ({ key: l.key, body: l.text }))} />
        </SheetSection>

        <p className="mt-4 border-t border-sage-300 pt-2 text-[0.65625rem] text-ink-soft">
          Prepared {proseDate(today)} from the client record. Details may have changed since; when in doubt, call
          the care manager.
        </p>
      </div>
    </PrintableSheetDialog>
  );
}
